import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/components/ParkMenu.css';
import { GraphContext } from '../contexts/GraphContext';
import { LocalTimeContext } from '../contexts/LocalTimeContext';
import { useLiquidGlass } from '../contexts/LiquidGlassContext';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import LiquidToggle from './LiquidToggle';
import { isMobile } from '../utils/deviceDetection';
import useSharedParkData from '../hooks/useSharedParkData';
import { ArrowUp, ArrowDown, Star, Users } from 'lucide-react';
import {
  applyCustomOrder,
  saveCustomParkOrder,
  resetParkOrder,
  hasCustomOrder,
  moveParkUp,
  moveParkDown
} from '../utils/parkOrderManager';
import { getAllParksData } from '../data/parksMenuData';

const CROWD_PREDICTION_PARK_PATHS = new Set([
  '/',
  '/studios',
  '/parc-asterix',
  '/efteling',
  '/walibi-holland',
  '/europa-park',
  '/phantasialand',
  '/plopsaland-de-panne',
  '/walibi-belgium',
  '/magic-kingdom',
  '/epcot',
  '/hollywood-studios',
  '/animal-kingdom',
  '/universal-studios',
  '/six-flags-qiddiyacity',
]);

const ParkMenu = ({
  isDarkMode,
  toggleTheme,
  isHighContrast,
  toggleHighContrast,
  currentLanguage,
  isLanguageMenuOpen,
  setIsLanguageMenuOpen,
  changeLanguage
}) => {

  const [isOpen, setIsOpen] = useState(false);
  const [selectedContinent, setSelectedContinent] = useState('europe');
  const [scrolled, setScrolled] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [tempParks, setTempParks] = useState([]);
  const [isChrome, setIsChrome] = useState(false);
  const [activeToggle, setActiveToggle] = useState(null);
  const { favorites: attractionFavorites, favoriteParkIds, toggleParkFavorite } = useSharedParkData();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setGraphEnabled } = useContext(GraphContext);
  const { showLocalTime, toggleLocalTime } = useContext(LocalTimeContext);
  const { liquidGlassEnabled, toggleLiquidGlass } = useLiquidGlass();
  const { user, profile } = useAuth();

  const isVipOrAdmin = profile?.role === 'vip' || profile?.role === 'admin';
  const showCrowdPredictionsOption = (() => {
    try {
      return localStorage.getItem('showCrowdPredictions') === 'true';
    } catch {
      return false;
    }
  })();
  const shouldShowCrowdPredictionIndicator = Boolean(user) && isVipOrAdmin && showCrowdPredictionsOption;

  const normalizeParkIdForMenu = (rawId) => {
    switch (rawId) {
      case 'DLP':
        return 'dlp';
      case 'WDS':
        return 'wds';
      case '85e3b542-af91-4f8a-8d28-445868a7c8fd':
        return 'ep';
      case 'efteling':
        return 'eft';
      case 'asterix':
        return 'ast';
      case 'nigloland':
        return 'niglo';
      case 'busch-gardens-tampa':
        return 'buschgardens';
      case 'legoland-florida':
        return 'legoland';
      case 'seaworld-orlando':
        return 'seaworld';
      default:
        return rawId;
    }
  };

  const favoriteParkIdsSet = useMemo(() => {
    const ids = new Set(
      Array.isArray(favoriteParkIds)
        ? favoriteParkIds.map((id) => normalizeParkIdForMenu(id))
        : []
    );

    if (Array.isArray(attractionFavorites)) {
      attractionFavorites.forEach((key) => {
        if (typeof key === 'string') {
          const [parkId] = key.split('_');
          if (parkId) {
            ids.add(normalizeParkIdForMenu(parkId));
          }
        }
      });
    }

    return ids;
  }, [attractionFavorites, favoriteParkIds]);

  const hasFavoriteParks = favoriteParkIdsSet.size > 0;

  // Chargement des données de parcs uniquement quand le menu est ouvert
  const allParksData = useMemo(() => {
    if (!isOpen) return { europe: [], northAmerica: [], asia: [] };
    return getAllParksData(t);
  }, [isOpen, t]);

  // Filtrer les parcs en fonction de l'état d'authentification, des favoris et du mode réorganisation
  const getFilteredParks = (continent) => {
    if (continent === 'favorites') {
      const allParks = [
        ...allParksData.europe,
        ...allParksData.northAmerica,
        ...allParksData.asia
      ];

      const favoriteParks = allParks.filter((park) => favoriteParkIdsSet.has(park.id));

      // En dehors du mode réorganisation, appliquer l'ordre personnalisé spécifique aux favoris
      if (!isReorderMode) {
        return applyCustomOrder(favoriteParks, 'favorites');
      }

      // En mode réorganisation sur "Mes favoris", utiliser la liste temporaire si définie
      return tempParks.length > 0 ? tempParks : favoriteParks;
    }

    if (!continent || !allParksData[continent]) return [];

    const parks = allParksData[continent];

    // Appliquer l'ordre personnalisé si pas en mode réorganisation
    if (!isReorderMode) {
      return applyCustomOrder(parks, continent);
    }

    // En mode réorganisation, utiliser tempParks si disponible
    return tempParks.length > 0 ? tempParks : parks;
  };

  useEffect(() => {
    // Always enable graphs for all users
    setGraphEnabled(true);
    localStorage.setItem('graphEnabled', JSON.stringify(true));
  }, [setGraphEnabled]);

  // Optimisation scroll handler avec throttle RAF
  const scrollTickingRef = useRef(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollTickingRef.current) {
        requestAnimationFrame(() => {
          const scrollPosition = window.scrollY;
          if (scrollPosition > 50) {
            setScrolled(true);
          } else {
            setScrolled(false);
          }
          scrollTickingRef.current = false;
        });
        scrollTickingRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Détection Chrome pour l'effet liquid glass
  useEffect(() => {
    const detectChrome = () => {
      const isChromeBrowser = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      setIsChrome(isChromeBrowser);
    };
    detectChrome();
  }, []);

  // Déterminer si l'effet liquid glass doit être appliqué
  // Désactiver automatiquement si l'utilisateur préfère réduire les animations
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shouldUseLiquidGlass = isChrome && liquidGlassEnabled && !prefersReducedMotion;

  const closeLanguageMenu = () => {
    if (typeof setIsLanguageMenuOpen === 'function') {
      setIsLanguageMenuOpen(false);
    }
  };

  const toggleMenu = () => {
    closeLanguageMenu();
    setIsOpen(!isOpen);
  };

  const handleParkSelect = (park) => {
    closeLanguageMenu();
    setIsOpen(false);
    setTimeout(() => {
      navigate(park.path);
    }, 300);
  };

  const handleContinentSelect = (continent) => {
    setSelectedContinent(continent);
    // Réinitialiser le mode réorganisation si on change de continent
    if (isReorderMode) {
      setIsReorderMode(false);
      setTempParks([]);
    }
  };

  const handleEnterReorderMode = () => {
    const currentParks = getFilteredParks(selectedContinent);
    setTempParks([...currentParks]);
    setIsReorderMode(true);
  };

  const handleSaveOrder = () => {
    const parkIds = tempParks.map(park => park.id);
    saveCustomParkOrder(selectedContinent, parkIds);
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleResetOrder = () => {
    resetParkOrder(selectedContinent);
    setIsReorderMode(false);
    setTempParks([]);
  };

  const handleMoveParkUp = (index) => {
    const newParks = moveParkUp(tempParks, index);
    setTempParks(newParks);
  };

  const handleMoveParkDown = (index) => {
    const newParks = moveParkDown(tempParks, index);
    setTempParks(newParks);
  };

  // Gestion du drag pour les toggle switches avec effet liquid glass
  const handleToggleMouseDown = (toggleId) => {
    if (!shouldUseLiquidGlass) return;
    setActiveToggle(toggleId);
  };

  const handleToggleMouseUp = () => {
    if (!shouldUseLiquidGlass) return;
    setTimeout(() => setActiveToggle(null), 50);
  };

  useEffect(() => {
    if (!shouldUseLiquidGlass) return;
    
    const handleGlobalMouseUp = () => {
      setActiveToggle(null);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [shouldUseLiquidGlass]);

  // Gestion de l'effet liquid glass sur les logos
  const handleLogoMouseMove = (e, logoElement) => {
    if (!shouldUseLiquidGlass) return;
    
    const rect = logoElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Effet highlight sur glass-specular
    const specular = logoElement.querySelector('.glass-specular');
    if (specular) {
      specular.style.background = `radial-gradient(
        circle at ${x}px ${y}px,
        rgba(255,255,255,0.15) 0%,
        rgba(255,255,255,0.05) 30%,
        rgba(255,255,255,0) 60%
      )`;
    }
  };

  const handleLogoMouseLeave = (logoElement) => {
    const specular = logoElement.querySelector('.glass-specular');
    if (specular) {
      specular.style.background = 'none';
    }
  };

  if (isMobile()) {
    return null;
  }

  return (
    <>
      {/* Filtres SVG Liquid Glass pour Chrome uniquement */}
      {shouldUseLiquidGlass && (
        <svg className="liquid-glass-svg-filter" aria-hidden="true">
          <defs>
            {/* Filtre Goo pour effet liquide amélioré */}
            <filter id="liquid-goo-filter">
              <feGaussianBlur in="SourceGraphic" stdDeviation="13" result="blur" />
              <feColorMatrix
                in="blur"
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 13 -10"
                result="goo"
              />
              <feComposite in="SourceGraphic" in2="goo" operator="atop" />
            </filter>

            {/* Filtre Liquid Glass Nav optimisé avec turbulence animée */}
            <filter id="liquid-glass-nav" x="0%" y="0%" width="100%" height="100%">
              <feTurbulence type="turbulence" baseFrequency="0.012" numOctaves="3" seed="2">
                <animate attributeName="baseFrequency" dur="6s" values="0.012;0.020;0.012" repeatCount="indefinite"/>
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" scale="20" xChannelSelector="R" yChannelSelector="G">
                <animate attributeName="scale" dur="6s" values="20;25;20" repeatCount="indefinite"/>
              </feDisplacementMap>
            </filter>

            {/* Filtre Reflection avancée */}
            <filter id="liquid-reflection-filter" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur"/>
              <feColorMatrix in="blur" type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        0 0 0 0.8 0" result="faded"/>
              <feComposite in="faded" in2="SourceGraphic" operator="over"/>
            </filter>

            {/* Filtre Transparence optimisée */}
            <filter id="liquid-transparency-filter">
              <feColorMatrix type="matrix" values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                0 0 0 0.85 0"/>
            </filter>

            {/* Filtre Distortion fluide */}
            <filter id="liquid-distortion-filter">
              <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise">
                <animate attributeName="baseFrequency" dur="6s" values="0.008;0.015;0.008" repeatCount="indefinite"/>
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="15"/>
            </filter>

            {/* Filtre Glow */}
            <filter id="liquid-glow-filter">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* Knockout pour backdrop */}
            <filter id="liquid-knockout-filter" colorInterpolationFilters="sRGB">
              <feColorMatrix
                result="knocked"
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        -1 -1 -1 1 0"
              />
              <feComponentTransfer>
                <feFuncR type="linear" slope="3" intercept="-1" />
                <feFuncG type="linear" slope="3" intercept="-1" />
                <feFuncB type="linear" slope="3" intercept="-1" />
              </feComponentTransfer>
              <feComponentTransfer>
                <feFuncR type="table" tableValues="0 0 0 0 0 1 1 1 1 1" />
                <feFuncG type="table" tableValues="0 0 0 0 0 1 1 1 1 1" />
                <feFuncB type="table" tableValues="0 0 0 0 0 1 1 1 1 1" />
              </feComponentTransfer>
            </filter>

            {/* Remove Black */}
            <filter id="liquid-remove-black-filter" colorInterpolationFilters="sRGB">
              <feColorMatrix
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        -255 -255 -255 0 1"
                result="black-pixels"
              />
              <feMorphology
                in="black-pixels"
                operator="dilate"
                radius="0.5"
                result="smoothed"
              />
              <feComposite in="SourceGraphic" in2="smoothed" operator="out" />
            </filter>

            {/* Glass Distortion amélioré pour les logos */}
            <filter id="glass-distortion">
              <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
            </filter>

            {/* Filtre MPT LG Dist pour le menu */}
            <filter id="mpt-lg-dist" x="0%" y="0%" width="100%" height="100%">
              <feTurbulence type="turbulence" baseFrequency="0.012" numOctaves="3" seed="2" />
              <feDisplacementMap in="SourceGraphic" scale="50" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
        </svg>
      )}

      <div className={`park-menu ${shouldUseLiquidGlass ? 'liquid-glass-chrome' : ''}`}>
        {/* Bouton d'ouverture quand le menu est fermé */}
        {!isOpen && (
          <div className={`menu-top-bar menu-top-bar-closed ${scrolled ? 'scrolled' : ''}`}>
            <div className="menu-left-controls">
              <button
                className={`hamburger ${isOpen ? 'active' : ''} ${scrolled ? 'scrolled' : ''}`}
                onClick={toggleMenu}
                aria-label="Menu"
              >
                <span></span>
                <span></span>
                <span></span>
              </button>
            </div>
          </div>
        )}

        <div className={`menu-content ${isOpen ? 'open' : ''}`}>
          {isOpen && (
            <div className={`menu-top-bar menu-top-bar-inside ${scrolled ? 'scrolled' : ''}`}>
              <div className="menu-left-controls">
                <button
                  className={`hamburger ${isOpen ? 'active' : ''} ${scrolled ? 'scrolled' : ''}`}
                  onClick={toggleMenu}
                  aria-label="Menu"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>

                <button
                  onClick={() => {
                    closeLanguageMenu();
                    navigate('/my-pass');
                    setIsOpen(false);
                  }}
                  className={`my-pass-button ${location.pathname === '/my-pass' ? 'active' : ''}`}
                  aria-label={t('myPass.menuTitle', 'Mon Pass Annuel')}
                >
                  <span
                    className="settings-icon my-pass-icon"
                    aria-hidden="true"
                    style={{
                      '--my-pass-icon-default': `url(${process.env.PUBLIC_URL}/images/buttons/Pass_BLACK.png)`,
                      '--my-pass-icon-active': `url(${process.env.PUBLIC_URL}/images/buttons/Pass_WHITE.png)`
                    }}
                  ></span>
                  <span className="my-pass-text">{t('myPass.menuTitle', 'Mon Pass Annuel')}</span>
                </button>
              </div>

              <div className="menu-right-controls">
                {typeof changeLanguage === 'function' && typeof setIsLanguageMenuOpen === 'function' && currentLanguage && (
                  <div className="language-selector menu-top-language-selector">
                    <button
                      type="button"
                      onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                      aria-label={t('changeLanguage')}
                    >
                      <picture>
                        <source srcSet={`/images/webp/${currentLanguage}-flag.webp`} type="image/webp" />
                        <img
                          src={`/images/${currentLanguage}-flag.png`}
                          alt={t(`languages.${currentLanguage}`)}
                          width="28"
                          height="19"
                        />
                      </picture>
                    </button>

                    {isLanguageMenuOpen && (
                      <div className="language-dropdown menu-top-language-dropdown">
                        {['fr', 'en', 'es', 'it', 'de'].map((lang) => (
                          <button
                            key={lang}
                            type="button"
                            onClick={() => {
                              changeLanguage(lang);
                              closeLanguageMenu();
                            }}
                          >
                            <picture>
                              <source srcSet={`/images/webp/${lang}-flag.webp`} type="image/webp" />
                              <img
                                src={`/images/${lang}-flag.png`}
                                alt={t(`languages.${lang}`)}
                                width="28"
                                height="19"
                              />
                            </picture>
                            <span>{lang.toUpperCase()}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <ThemeToggle isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
              </div>
            </div>
          )}

          <div className="menu-scroll-container">


            <div className="continent-selector">
              <button
                className={`continent-button ${selectedContinent === 'europe' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('europe')}
              >
                {t('continents.europe')}
              </button>
              <button
                className={`continent-button ${selectedContinent === 'northAmerica' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('northAmerica')}
              >
                {t('continents.northAmerica')}
              </button>
              <button
                className={`continent-button ${selectedContinent === 'asia' ? 'active' : ''}`}
                onClick={() => handleContinentSelect('asia')}
              >
                {t('continents.asia')}
              </button>
              <button
                className={`continent-button ${selectedContinent === 'favorites' ? 'active' : ''} ${!hasFavoriteParks ? 'disabled' : ''}`}
                onClick={() => hasFavoriteParks && handleContinentSelect('favorites')}
                disabled={!hasFavoriteParks}
              >
                {t('continents.favorites')}
              </button>
            </div>

            {/* Boutons de contrôle du mode réorganisation */}
            {!isReorderMode && (
              <div className="reorder-controls">
                <button 
                  onClick={handleEnterReorderMode}
                  className="menu-item reorder-btn icon-only"
                  title={t('menu.reorderMode')}
                  aria-label={t('menu.reorderMode')}
                >
                  ✏️
                  <span className="tooltip">{t('menu.reorderMode')}</span>
                </button>
                {hasCustomOrder(selectedContinent) && (
                  <button 
                    onClick={handleResetOrder}
                    className="menu-item reset-order-btn icon-only"
                    title={t('menu.resetOrder')}
                    aria-label={t('menu.resetOrder')}
                  >
                    ↺
                    <span className="tooltip">{t('menu.resetOrder')}</span>
                  </button>
                )}
              </div>
            )}

            {isReorderMode && (
              <div className="reorder-mode-info">
                <div className="reorder-mode-header">
                  <span className="reorder-mode-title">✨ {t('menu.reorderModeActive')}</span>
                  <p className="reorder-mode-text">{t('menu.reorderInfo')}</p>
                </div>
                <div className="reorder-actions">
                  <button 
                    onClick={handleSaveOrder}
                    className="menu-item save-order-btn"
                  >
                    ✓ {t('menu.saveOrder')}
                  </button>
                  <button 
                    onClick={handleCancelReorder}
                    className="menu-item cancel-order-btn"
                  >
                    ✕ {t('menu.cancelOrder')}
                  </button>
                </div>
              </div>
            )}

            <div className={`parks-grid ${isReorderMode ? 'reorder-mode' : ''}`}>
              {getFilteredParks(selectedContinent).map((park, index) => (
                <div
                  key={park.id}
                  className={`park-logo-container ${isReorderMode ? 'reorder-active' : ''}`}
                >
                  {/* Couches de l'effet liquid glass - uniquement si activé et hors mode réorganisation */}
                  {shouldUseLiquidGlass && !isReorderMode && (
                    <>
                      <div className="glass-filter"></div>
                      <div className="glass-overlay"></div>
                      <div className="glass-specular"></div>
                    </>
                  )}
                  
                  {isReorderMode && (
                    <div className="reorder-buttons">
                      <button
                        onClick={() => handleMoveParkUp(index)}
                        disabled={index === 0}
                        className="move-btn move-up"
                        aria-label={t('menu.moveUp')}
                        title={t('menu.moveUp')}
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMoveParkDown(index)}
                        disabled={index === getFilteredParks(selectedContinent).length - 1}
                        className="move-btn move-down"
                        aria-label={t('menu.moveDown')}
                        title={t('menu.moveDown')}
                      >
                        <ArrowDown size={16} />
                      </button>
                      {selectedContinent !== 'favorites' && (
                        <button
                          onClick={() => toggleParkFavorite(park.id)}
                          className="park-favorite-btn"
                          aria-pressed={favoriteParkIdsSet.has(park.id)}
                          aria-label={favoriteParkIdsSet.has(park.id) ? t('menu.removeParkFavorite') : t('menu.addParkFavorite')}
                          title={favoriteParkIdsSet.has(park.id) ? t('menu.removeParkFavorite') : t('menu.addParkFavorite')}
                        >
                          <Star
                            className={`favorite-icon ${favoriteParkIdsSet.has(park.id) ? 'active' : ''}`}
                            fill={favoriteParkIdsSet.has(park.id) ? 'currentColor' : 'none'}
                          />
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                  <div
                    onClick={() => !isReorderMode && handleParkSelect(park)}
                    onKeyDown={(e) => {
                      if (!isReorderMode && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleParkSelect(park);
                      }
                    }}
                    className="park-logo-wrapper"
                    style={{ cursor: isReorderMode ? 'default' : 'pointer' }}
                    role={!isReorderMode ? 'button' : undefined}
                    tabIndex={!isReorderMode ? 0 : undefined}
                    aria-label={!isReorderMode ? `Sélectionner ${park.name}` : undefined}
                    onMouseMove={(e) => shouldUseLiquidGlass && !isReorderMode && handleLogoMouseMove(e, e.currentTarget.parentElement)}
                    onMouseLeave={(e) => shouldUseLiquidGlass && !isReorderMode && handleLogoMouseLeave(e.currentTarget.parentElement)}
                  >
                    <picture>
                      <source srcSet={park.logo.replace('.png', '.webp').replace('/logos/', '/logos/webp/')} type="image/webp" />
                      <source srcSet={park.logo} type="image/png" />
                      <img
                        src={park.logo} // Fallback pour les anciens navigateurs
                        alt={park.name}
                        className={`park-logo ${location.pathname === park.path ? 'active' : ''}`}
                        title={park.name}
                        width="200"
                        height="112"
                        fetchpriority={index < 4 ? "high" : "auto"}
                        loading={index < 4 ? "eager" : "lazy"}
                      />
                    </picture>
                  </div>
                  {!isReorderMode && shouldShowCrowdPredictionIndicator && CROWD_PREDICTION_PARK_PATHS.has(park.path) && (
                    <span
                      className="crowd-prediction-indicator"
                      title="Prédictions d'affluence disponibles"
                      aria-label="Prédictions d'affluence disponibles"
                    >
                      <Users size={12} aria-hidden="true" />
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="app-settings">
              <div className="settings-items">
                {/* Toggle pour activer/désactiver l'effet liquid glass (Chrome uniquement) */}
                {isChrome && (
                  <div className="admin-toggle-container">
                    <LiquidToggle
                      checked={liquidGlassEnabled}
                      onChange={toggleLiquidGlass}
                      label={t('settings.liquidGlass', 'Effet Liquid Glass')}
                      ariaLabel={t('settings.liquidGlass', 'Activer/Désactiver l\'effet liquid glass')}
                      isActive={activeToggle === 'liquidGlass'}
                      onMouseDown={() => handleToggleMouseDown('liquidGlass')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('liquidGlass')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  </div>
                )}

                <div className="admin-toggle-container">
                  {shouldUseLiquidGlass ? (
                    <LiquidToggle
                      checked={showLocalTime}
                      onChange={toggleLocalTime}
                      label={t('localTimeToggle')}
                      ariaLabel={t('localTimeToggle')}
                      isActive={activeToggle === 'localTime'}
                      onMouseDown={() => handleToggleMouseDown('localTime')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('localTime')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  ) : (
                    <label className="toggle-switch">
                      <span>{t('localTimeToggle')}</span>
                      <input
                        type="checkbox"
                        checked={showLocalTime}
                        onChange={toggleLocalTime}
                        aria-label={t('localTimeToggle')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>


                <div className="admin-toggle-container">
                  {shouldUseLiquidGlass ? (
                    <LiquidToggle
                      checked={!!isHighContrast}
                      onChange={toggleHighContrast}
                      label={t('settings.accessibility.highContrast', 'Contraste élevé')}
                      ariaLabel={t('settings.accessibility.highContrast', 'Activer le mode contraste élevé')}
                      isActive={activeToggle === 'highContrast'}
                      onMouseDown={() => handleToggleMouseDown('highContrast')}
                      onMouseUp={handleToggleMouseUp}
                      onTouchStart={() => handleToggleMouseDown('highContrast')}
                      onTouchEnd={handleToggleMouseUp}
                    />
                  ) : (
                    <label className="toggle-switch">
                      <span>{t('settings.accessibility.highContrast', 'Contraste élevé')}</span>
                      <input
                        type="checkbox"
                        checked={!!isHighContrast}
                        onChange={toggleHighContrast}
                        aria-label={t('settings.accessibility.highContrast', 'Activer le mode contraste élevé')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>

              </div>
            </div>

            <div className="about-link-container">
              <Link to="/about" className="menu-item about-link">
                {t('legal.title')}
              </Link>
              <a
                href="https://lite.magicparktimes.com"
                className="menu-item lite-link"
                target="_blank"
                rel="noopener noreferrer"
                title={t('lite.title')}
              >
                🚀 Version Lite
              </a>
              <Link to="/support-us" className="menu-item support-link">
                💝 {t('supportUs.menuTitle', 'Nous soutenir')}
              </Link>
            </div>
          </div>
        </div>
        
        {isOpen && <div className={`overlay ${isOpen ? 'visible' : ''}`} onClick={toggleMenu} onKeyDown={(e) => { if (e.key === 'Escape') toggleMenu(); }} role="button" tabIndex={0} aria-label="Fermer le menu"></div>}
      </div>
    </>
  );
};

export default ParkMenu;
